import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { 
  useV1ProductionProductionOrdersCreate, 
  useV1InventoryMaterialRevisionsList,
  useV1ProductionBomRevisionsList,
  useV1ProductionRoutingRevisionsList,
  useV1ProductionRoutingFamiliesList
} from "@/api/generated/v1/v1";
import { usePermission } from "@/auth/usePermission";
import { CustomFieldsPanel } from "@/features/customFields/CustomFieldsPanel";
import { setCustomFieldValues } from "@/features/customFields/api";
import { PageBanner } from "@/shared/components/PageBanner";

function parseLocalDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  const parts = dateStr.split("-").map(Number);
  const [y, m, d] = parts;
  if (parts.length !== 3 || y === undefined || m === undefined || d === undefined || parts.some(isNaN)) return null;
  return new Date(y, m - 1, d);
}

function formatLocalDate(date: DateObject | null): string {
  if (!date) return "";
  const jsDate = date.toDate();
  const year = jsDate.getFullYear();
  const month = String(jsDate.getMonth() + 1).padStart(2, "0");
  const day = String(jsDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function ProductionOrderCreatePage() {
  const navigate = useNavigate();
  const canCreate = usePermission("production.manage");

  const [formData, setFormData] = useState({
    order_number: "",
    material_revision: "",
    bom_revision: "",
    routing_revision: "",
    planned_quantity: "",
    due_date: "",
    priority: "normal",
  });

  const [customFieldValues, setCustomFieldValuesState] = useState<Record<string, any>>({});
  const [customFieldsValid, setCustomFieldsValid] = useState(true);

  // Query only produced materials (Finished & Semi-Finished products) with page_size=200 to prevent truncation
  const { data: materialsData } = useV1InventoryMaterialRevisionsList({ 
    is_active: "True", 
    status: "published",
    material_type__in: "finished_product,semi_finished",
    page_size: 200,
  } as any);

  const { data: bomsData } = useV1ProductionBomRevisionsList({ status: "published", page_size: 200 } as any);
  const { data: routingsData } = useV1ProductionRoutingRevisionsList({ status: "published", page_size: 200 } as any);
  const { data: routingFamiliesData } = useV1ProductionRoutingFamiliesList({ page_size: 200 } as any);

  const { mutateAsync: createOrder, isPending } = useV1ProductionProductionOrdersCreate();

  // Create a fast lookup map for Routing Families by ID
  const routingFamiliesMap = useMemo(() => {
    const map: Record<string, any> = {};
    (routingFamiliesData?.results || []).forEach((f: any) => {
      map[f.id] = f;
    });
    return map;
  }, [routingFamiliesData]);

  // Filter materials to ONLY produced items (Finished Products & Semi-Finished Products)
  const buildableMaterials = useMemo(() => {
    if (!materialsData?.results) return [];
    return materialsData.results.filter((m: any) => {
      const type = m.material_type || m.family_display?.material_type || m.family?.material_type;
      return type === "finished_product" || type === "semi_finished" || !type;
    });
  }, [materialsData]);

  // Filter BOMs matching the selected material output if a material is chosen
  const filteredBoms = useMemo(() => {
    if (!bomsData?.results) return [];
    if (!formData.material_revision) return bomsData.results;
    return bomsData.results.filter((b: any) => {
      const outMatId = b.bom_family_display?.output_material_revision_id || b.family_display?.output_material_revision_id;
      return !outMatId || outMatId === formData.material_revision;
    });
  }, [bomsData, formData.material_revision]);

  const getMaterialTypeLabel = (m: any) => {
    const type = m.material_type || m.family_display?.material_type || m.family?.material_type;
    if (type === "finished_product") return " 📦 (محصول نهایی)";
    if (type === "semi_finished") return " ⚙️ (محصول نیمه‌ساخته)";
    return "";
  };

  const getBomLabel = (b: any) => {
    const famName = b.bom_family_display?.name || b.family_display?.name || b.bom_family_name || (typeof b.family === "object" ? b.family?.name : "") || "";
    const famCode = b.bom_family_display?.code || b.family_display?.code || b.bom_family_code || (typeof b.family === "object" ? b.family?.code : "") || "";

    const namePart = famName ? `${famName} ${famCode ? `(${famCode})` : ""}` : (famCode || (b.revision_name ? b.revision_name : `BOM`));
    const revPart = `نسخه ${b.revision_no}`;

    return `${namePart} - ${revPart}`;
  };

  const getRoutingLabel = (r: any) => {
    const famObj = typeof r.family === "object" ? r.family : routingFamiliesMap[r.family];
    const famName = r.routing_family_display?.name || r.family_display?.name || r.family_name || famObj?.name || "";
    const famCode = r.routing_family_display?.code || r.family_display?.code || r.family_code || famObj?.code || "";

    const namePart = famName ? `${famName} ${famCode ? `(${famCode})` : ""}` : (famCode || `مسیر تولید`);
    const revPart = r.revision_name ? `نسخه ${r.revision_no} (${r.revision_name})` : `نسخه ${r.revision_no}`;

    return `${namePart} - ${revPart}`;
  };

  const generateAutoOrderNumber = () => {
    const today = new Date();
    const year = today.toLocaleDateString("fa-IR-u-nu-latn", { year: "numeric" });
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    setFormData((prev) => ({
      ...prev,
      order_number: `ORD-${year}-${randomNum}`,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreate) return;
    if (!formData.due_date) {
      alert("لطفاً موعد تحویل را انتخاب کنید.");
      return;
    }
    if (!customFieldsValid) { alert("لطفاً فیلدهای سفارشی الزامی را پر کنید."); return; }

    try {
      const res = await createOrder({
        data: {
          order_number: formData.order_number,
          material_revision: formData.material_revision,
          bom_revision: formData.bom_revision,
          routing_revision: formData.routing_revision,
          planned_quantity: Number(formData.planned_quantity) as any,
          due_date: formData.due_date as any,
          priority: formData.priority as any,
        }
      });
      
      if (Object.keys(customFieldValues).length > 0) {
        await setCustomFieldValues("production.productionorder", res.id, customFieldValues);
      }

      navigate(`/production/orders/${res.id}`);
    } catch (err: any) {
      alert("خطا در ثبت سفارش: " + JSON.stringify(err.response?.data || err.message));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  if (!canCreate) {
    return (
      <div style={{ padding: "2rem", color: "#EF4444", fontWeight: 700, textAlign: "center" }}>
        عدم دسترسی کافی جهت ایجاد سفارش
      </div>
    );
  }

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <style>{`
        .form-input-control {
          width: 100%;
          padding: 0.65rem 0.9rem;
          border-radius: 10px;
          border: 1px solid var(--color-border, #E4DCC8);
          background: #FFFFFF;
          color: var(--color-text-main, #2B2B2B);
          font-size: 0.9rem;
          font-weight: 600;
          outline: none;
          transition: all 0.2s ease;
        }
        .form-input-control:focus {
          border-color: #007270;
          box-shadow: 0 0 0 3px rgba(0, 114, 112, 0.15);
        }
      `}</style>

      {/* 1. PAGE BANNER */}
      <PageBanner
        title="ایجاد سفارش تولید جدید"
        subtitle="تعریف سفارش جدید فقط برای کالاها و محصولات قابل تولید (محصول نهایی و نیمه‌ساخته)"
        icon="⚡"
        helpLink="/help/production/orders/create"
      >
        <button
          onClick={() => navigate("/production/orders")}
          style={{
            padding: "0.6rem 1.1rem",
            borderRadius: "12px",
            background: "rgba(255, 255, 255, 0.15)",
            color: "#ffffff",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            fontWeight: 700,
            fontSize: "0.9rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            transition: "all 0.2s ease",
          }}
        >
          <span>←</span>
          <span>بازگشت به سفارشات</span>
        </button>
      </PageBanner>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        {/* CARD 1: BASIC INFORMATION */}
        <div className="brand-card-extand" style={{ padding: "1.5rem", overflow: "visible" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--color-border, #E4DCC8)", paddingBottom: "0.75rem" }}>
            <span style={{ fontSize: "1.3rem" }}>📋</span>
            <div>
              <h2 style={{ fontSize: "1.05rem", fontWeight: "800", color: "#007270", margin: 0 }}>
                اطلاعات پایه و شناسه سفارش
              </h2>
              <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted, #666)" }}>
                شماره شناسه سفارش تولید و محصول مورد نظر را تعیین فرمایید.
              </span>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1.25rem" }}>
            {/* ORDER NUMBER */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                  شماره سفارش <span style={{ color: "#EF4444" }}>*</span>
                </label>
                <button
                  type="button"
                  onClick={generateAutoOrderNumber}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#007270",
                    fontSize: "0.78rem",
                    fontWeight: "700",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  ⚡ شماره خودکار
                </button>
              </div>
              <input
                type="text"
                name="order_number"
                value={formData.order_number}
                onChange={handleChange}
                placeholder="مثال: ORD-1403-001"
                className="form-input-control"
                style={{ direction: "ltr", textAlign: "left" }}
                required
              />
            </div>

            {/* MATERIAL REVISION */}
            <div>
              <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                کالای قابل تولید (محصول نهایی / نیمه‌ساخته) <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <select
                name="material_revision"
                value={formData.material_revision}
                onChange={handleChange}
                className="form-input-control"
                required
              >
                <option value="">یک محصول نهایی یا نیمه‌ساخته انتخاب کنید...</option>
                {buildableMaterials.map((m: any) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.code || m.family_code || m.sku || "بدون کد"}){getMaterialTypeLabel(m)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            style={{
              marginTop: "1rem",
              padding: "0.6rem 0.9rem",
              borderRadius: "8px",
              background: "rgba(0, 114, 112, 0.06)",
              border: "1px solid rgba(0, 114, 112, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <span style={{ fontSize: "0.9rem" }}>💡</span>
            <span style={{ fontSize: "0.78rem", color: "#007270", fontWeight: "600" }}>
              طبق قواعد مدیریت تولید کارخانه، سفارشات تولید فقط برای محصولات نهایی و قطعات نیمه‌ساخته قابل ثبت هستند.
            </span>
          </div>
        </div>

        {/* CARD 2: BOM & ROUTING TECHNICAL SPECIFICATIONS */}
        <div className="brand-card-extand" style={{ padding: "1.5rem", overflow: "visible" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--color-border, #E4DCC8)", paddingBottom: "0.75rem" }}>
            <span style={{ fontSize: "1.3rem" }}>⚙️</span>
            <div>
              <h2 style={{ fontSize: "1.05rem", fontWeight: "800", color: "#007270", margin: 0 }}>
                فرمول ساخت (BOM) و مسیر تولید (Routing)
              </h2>
              <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted, #666)" }}>
                نسخه فرمول مواد اولیه و ایستگاه‌های عملیاتی ساخت را انتخاب فرمایید.
              </span>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1.25rem" }}>
            {/* BOM REVISION */}
            <div>
              <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                BOM (ریویژن منتشرشده) <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <select
                name="bom_revision"
                value={formData.bom_revision}
                onChange={handleChange}
                className="form-input-control"
                required
              >
                <option value="">انتخاب نسخه فرمول مواد (BOM)...</option>
                {filteredBoms.map((b: any) => (
                  <option key={b.id} value={b.id}>
                    {getBomLabel(b)}
                  </option>
                ))}
              </select>
            </div>

            {/* ROUTING REVISION */}
            <div>
              <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                مسیر تولید (Routing) <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <select
                name="routing_revision"
                value={formData.routing_revision}
                onChange={handleChange}
                className="form-input-control"
                required
              >
                <option value="">انتخاب نسخه مسیر تولید (Routing)...</option>
                {routingsData?.results?.map((r: any) => (
                  <option key={r.id} value={r.id}>
                    {getRoutingLabel(r)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* CARD 3: PLANNING & SCHEDULING & PRIORITY */}
        <div className="brand-card-extand" style={{ padding: "1.5rem", overflow: "visible" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--color-border, #E4DCC8)", paddingBottom: "0.75rem" }}>
            <span style={{ fontSize: "1.3rem" }}>📅</span>
            <div>
              <h2 style={{ fontSize: "1.05rem", fontWeight: "800", color: "#007270", margin: 0 }}>
                برنامه‌ریزی، موعد تحویل و اولویت
              </h2>
              <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted, #666)" }}>
                تعداد مورد انتظار، تاریخ تحویل نهایی و سطح اهمیت سفارش را تعیین نمایید.
              </span>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.25rem" }}>
            {/* PLANNED QUANTITY */}
            <div>
              <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                تعداد برنامه‌ریزی‌شده <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <input
                type="number"
                name="planned_quantity"
                value={formData.planned_quantity}
                onChange={handleChange}
                placeholder="100"
                className="form-input-control"
                required
                min="1"
                step="any"
              />
            </div>

            {/* DUE DATE */}
            <div>
              <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                موعد تحویل (هجری شمسی) <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <div style={{ position: "relative", width: "100%" }}>
                <DatePicker
                  calendar={persian}
                  locale={persian_fa}
                  calendarPosition="bottom-right"
                  portal
                  value={parseLocalDate(formData.due_date)}
                  onChange={(date: DateObject | null) => {
                    setFormData((prev) => ({
                      ...prev,
                      due_date: formatLocalDate(date),
                    }));
                  }}
                  inputClass="form-input-control"
                  containerStyle={{ width: "100%" }}
                  style={{ width: "100%", height: "42px" }}
                  placeholder="انتخاب موعد تحویل (شمسی)..."
                />
              </div>
            </div>

            {/* PRIORITY */}
            <div>
              <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>
                اولویت اجرا
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="form-input-control"
              >
                <option value="low">⚪ پایین</option>
                <option value="normal">🔹 عادی</option>
                <option value="high">⚡ بالا</option>
                <option value="urgent">🚨 فوری</option>
              </select>
            </div>
          </div>
        </div>

        {/* CARD 4: CUSTOM FIELDS PANEL */}
        <div className="brand-card-extand" style={{ padding: "1.5rem", overflow: "visible" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--color-border, #E4DCC8)", paddingBottom: "0.75rem" }}>
            <span style={{ fontSize: "1.3rem" }}>🏷️</span>
            <div>
              <h2 style={{ fontSize: "1.05rem", fontWeight: "800", color: "#007270", margin: 0 }}>
                ویژگی‌ها و فیلدهای سفارشی
              </h2>
              <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted, #666)" }}>
                مشخصات تکمیلی تعریف‌شده برای سفارشات تولید کارخانه
              </span>
            </div>
          </div>

          <CustomFieldsPanel
            targetContentType="production.productionorder"
            hideSaveButton={true}
            onChange={(values, isValid) => {
              setCustomFieldValuesState(values);
              setCustomFieldsValid(isValid);
            }}
          />
        </div>

        {/* FOOTER ACTIONS */}
        <div
          className="brand-card-extand"
          style={{
            padding: "1.25rem 1.5rem",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "1rem",
            background: "#ffffff",
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/production/orders")}
            disabled={isPending}
            style={{
              padding: "0.65rem 1.4rem",
              borderRadius: "10px",
              background: "transparent",
              border: "1px solid var(--color-border, #E4DCC8)",
              color: "var(--color-text-main, #2B2B2B)",
              fontSize: "0.9rem",
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            انصراف
          </button>
          <button
            type="submit"
            disabled={isPending}
            style={{
              padding: "0.65rem 1.8rem",
              borderRadius: "10px",
              background: "#007270",
              color: "#FFFFFF",
              border: "none",
              fontSize: "0.95rem",
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(0, 114, 112, 0.25)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>{isPending ? "⏳ در حال ثبت سفارش..." : "⚡ ثبت سفارش تولید"}</span>
          </button>
        </div>
      </form>
    </div>
  );
}
